import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Menu,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Stack,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Notifications as NotificationIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  MonetizationOn as FeeIcon,
  Info as InfoIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon
} from '@mui/icons-material';
import axios from 'axios';

const Notifications = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    notification_type: 'general',
    target_audience: 'all',
    batch_id: '',
    student_id: ''
  });

  const notificationTypes = [
    { value: 'class_update', label: 'Class Update', icon: <ScheduleIcon /> },
    { value: 'schedule_change', label: 'Schedule Change', icon: <ScheduleIcon /> },
    { value: 'event', label: 'Event', icon: <EventIcon /> },
    { value: 'fee_reminder', label: 'Fee Reminder', icon: <FeeIcon /> },
    { value: 'general', label: 'General', icon: <InfoIcon /> }
  ];

  useEffect(() => {
    fetchNotifications();
    fetchStudents();
    fetchBatches();
  }, []);

  useEffect(() => {
    filterNotifications();
  }, [notifications, activeTab]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/notifications/', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      showSnackbar('Error fetching notifications', 'error');
    }
  };

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/admin/students', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudents(response.data.students || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/admin/batches', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(response.data.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const filterNotifications = () => {
    if (activeTab === 'all') {
      setFilteredNotifications(notifications);
    } else if (activeTab === 'archived') {
      setFilteredNotifications(notifications.filter(n => n.is_archived));
    } else {
      setFilteredNotifications(notifications.filter(n => n.notification_type === activeTab && !n.is_archived));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (notification = null) => {
    if (notification) {
      setEditingNotification(notification);
      setFormData({
        title: notification.title || '',
        message: notification.message || '',
        notification_type: notification.notification_type || 'general',
        target_audience: notification.target_audience || 'all',
        batch_id: notification.batch_id || '',
        student_id: notification.student_id || ''
      });
    } else {
      setEditingNotification(null);
      setFormData({
        title: '',
        message: '',
        notification_type: 'general',
        target_audience: 'all',
        batch_id: '',
        student_id: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNotification(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Reset batch_id and student_id when target_audience changes
      if (name === 'target_audience') {
        if (value === 'all') {
          updated.batch_id = '';
          updated.student_id = '';
        } else if (value === 'batch_specific') {
          updated.student_id = '';
        } else if (value === 'individual') {
          updated.batch_id = '';
        }
      }
      
      return updated;
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.title || !formData.message) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      if (formData.target_audience === 'batch_specific' && !formData.batch_id) {
        showSnackbar('Please select a batch', 'error');
        return;
      }

      if (formData.target_audience === 'individual' && !formData.student_id) {
        showSnackbar('Please select a student', 'error');
        return;
      }

      const token = localStorage.getItem('access_token');
      
      // Prepare data - ensure batch_id and student_id are null when not applicable
      const submitData = {
        title: formData.title,
        message: formData.message,
        notification_type: formData.notification_type,
        target_audience: formData.target_audience
      };

      // Only include batch_id if batch_specific
      if (formData.target_audience === 'batch_specific' && formData.batch_id) {
        submitData.batch_id = parseInt(formData.batch_id);
      }

      // Only include student_id if individual
      if (formData.target_audience === 'individual' && formData.student_id) {
        submitData.student_id = parseInt(formData.student_id);
      }

      const url = editingNotification
        ? `/api/notifications/${editingNotification.id}`
        : '/api/notifications/';
      
      const method = editingNotification ? 'put' : 'post';

      await axios[method](url, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showSnackbar(
        editingNotification ? 'Notification updated successfully' : 'Notification sent successfully',
        'success'
      );
      
      handleCloseDialog();
      fetchNotifications();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving notification', 'error');
    }
  };

  const handleArchiveToggle = async (notification) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `/api/notifications/${notification.id}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showSnackbar(
        notification.is_archived ? 'Notification unarchived' : 'Notification archived',
        'success'
      );
      handleCloseMenu();
      fetchNotifications();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error archiving notification', 'error');
    }
  };

  const handleDeleteNotification = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/notifications/${selectedNotification.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showSnackbar('Notification deleted successfully', 'success');
      handleCloseMenu();
      fetchNotifications();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting notification', 'error');
    }
  };

  const handleMenuOpen = (event, notification) => {
    setAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedNotification(null);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getNotificationTypeIcon = (type) => {
    const notifType = notificationTypes.find(t => t.value === type);
    return notifType ? notifType.icon : <NotificationIcon />;
  };

  const getNotificationTypeLabel = (type) => {
    const notifType = notificationTypes.find(t => t.value === type);
    return notifType ? notifType.label : type;
  };

  const getTargetAudienceLabel = (notification) => {
    if (notification.target_audience === 'all') return 'All Students';
    if (notification.target_audience === 'batch_specific') {
      const batch = batches.find(b => b.id === notification.batch_id);
      return batch ? `Batch: ${batch.name}` : 'Specific Batch';
    }
    if (notification.target_audience === 'individual') {
      const student = students.find(s => s.id === notification.student_id);
      return student ? `Student: ${student.student_name}` : 'Individual Student';
    }
    return notification.target_audience;
  };

  const getNotificationStats = () => {
    const stats = {
      total: notifications.length,
      active: notifications.filter(n => !n.is_archived).length,
      archived: notifications.filter(n => n.is_archived).length
    };
    
    notificationTypes.forEach(type => {
      stats[type.value] = notifications.filter(n => n.notification_type === type.value && !n.is_archived).length;
    });
    
    return stats;
  };

  const stats = getNotificationStats();

  return (
    <AdminLayout>
      <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 3 
        }}>
          <Typography variant="h4" fontWeight={700}>
            Notifications
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            fullWidth={isMobile}
          >
            Send Notification
          </Button>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4, width: '100%', ml: 0 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.total}
                    </Typography>
                    <Typography variant="body2">Total Notifications</Typography>
                  </Box>
                  <NotificationIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.active}
                    </Typography>
                    <Typography variant="body2">Active</Typography>
                  </Box>
                  <NotificationIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.archived}
                    </Typography>
                    <Typography variant="body2">Archived</Typography>
                  </Box>
                  <ArchiveIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'info.light', color: 'info.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.fee_reminder}
                    </Typography>
                    <Typography variant="body2">Fee Reminders</Typography>
                  </Box>
                  <FeeIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filter Tabs */}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All" value="all" />
            {notificationTypes.map(type => (
              <Tab
                key={type.value}
                label={`${type.label} (${stats[type.value] || 0})`}
                value={type.value}
                icon={type.icon}
                iconPosition="start"
              />
            ))}
            <Tab
              label={`Archived (${stats.archived})`}
              value="archived"
              icon={<ArchiveIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Card>

        {/* Notifications Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {activeTab === 'all' ? 'All Notifications' : activeTab === 'archived' ? 'Archived Notifications' : getNotificationTypeLabel(activeTab)}
            </Typography>
            {isMobile ? (
              /* Mobile Card View */
              <Stack spacing={2} sx={{ mt: 2 }}>
                {filteredNotifications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" py={3}>
                    No notifications found. Click "Send Notification" to create one.
                  </Typography>
                ) : (
                  filteredNotifications.map((notification) => (
                    <Card 
                      key={notification.id} 
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
                        border: '1px solid',
                        borderColor: 'rgba(212, 175, 55, 0.2)',
                        boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 16px rgba(212, 175, 55, 0.25)',
                          transform: 'translateY(-2px)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '4px',
                          height: '100%',
                          background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
                        },
                      }}
                    >
                      <CardContent sx={{ pl: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#2C2416', fontSize: '1.1rem' }}>
                              {notification.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#8B7355', fontSize: '0.8rem' }}>
                              {notification.message.substring(0, 60)}
                              {notification.message.length > 60 ? '...' : ''}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, notification)}
                            sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212, 175, 55, 0.1)' } }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>
                        
                        <Divider sx={{ my: 1.5, borderColor: 'rgba(212, 175, 55, 0.15)' }} />
                        
                        <Stack spacing={1.5}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: '#D4AF37' }}>
                              {getNotificationTypeIcon(notification.notification_type)}
                            </Box>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#2C2416' }}>
                              {getNotificationTypeLabel(notification.notification_type)}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={getTargetAudienceLabel(notification)}
                              size="small"
                              sx={{ bgcolor: 'rgba(212, 175, 55, 0.1)', color: '#8B4513', borderColor: 'rgba(212, 175, 55, 0.3)', fontWeight: 600 }}
                              variant="outlined"
                            />
                            {notification.is_archived ? (
                              <Chip
                                label="Archived"
                                color="default"
                                size="small"
                                icon={<ArchiveIcon />}
                              />
                            ) : (
                              <Chip
                                label="Active"
                                color="success"
                                size="small"
                              />
                            )}
                          </Box>
                          
                          <Typography variant="caption" sx={{ color: '#8B7355', fontSize: '0.75rem' }}>
                            {new Date(notification.created_at).toLocaleDateString('en-IN')}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Stack>
            ) : (
              /* Desktop Table View */
              <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Title</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Target Audience</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredNotifications.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No notifications found. Click "Send Notification" to create one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredNotifications.map((notification) => (
                      <TableRow key={notification.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {notification.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {notification.message.substring(0, 60)}
                            {notification.message.length > 60 ? '...' : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getNotificationTypeIcon(notification.notification_type)}
                            <Typography variant="body2">
                              {getNotificationTypeLabel(notification.notification_type)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getTargetAudienceLabel(notification)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(notification.created_at).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell>
                          {notification.is_archived ? (
                            <Chip
                              label="Archived"
                              color="default"
                              size="small"
                              icon={<ArchiveIcon />}
                            />
                          ) : (
                            <Chip
                              label="Active"
                              color="success"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, notification)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Notification Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingNotification ? 'Edit Notification' : 'Send New Notification'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Title *"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                fullWidth
                placeholder="e.g., Class Rescheduled - Beginner Batch"
              />

              <TextField
                label="Message *"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={4}
                placeholder="Enter the notification message..."
              />

              <TextField
                select
                label="Notification Type *"
                name="notification_type"
                value={formData.notification_type}
                onChange={handleInputChange}
                fullWidth
              >
                {notificationTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {type.icon}
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              <FormControl component="fieldset">
                <FormLabel component="legend">Target Audience *</FormLabel>
                <RadioGroup
                  name="target_audience"
                  value={formData.target_audience}
                  onChange={handleInputChange}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All Students"
                  />
                  <FormControlLabel
                    value="batch_specific"
                    control={<Radio />}
                    label="Specific Batch"
                  />
                  <FormControlLabel
                    value="individual"
                    control={<Radio />}
                    label="Individual Student"
                  />
                </RadioGroup>
              </FormControl>

              {formData.target_audience === 'batch_specific' && (
                <TextField
                  select
                  label="Select Batch *"
                  name="batch_id"
                  value={formData.batch_id}
                  onChange={handleInputChange}
                  fullWidth
                  helperText={batches.length === 0 ? "No batches available" : ""}
                >
                  {batches.length === 0 ? (
                    <MenuItem value="" disabled>No batches available</MenuItem>
                  ) : (
                    batches.map((batch) => (
                      <MenuItem key={batch.id} value={batch.id}>
                        {batch.name} - {batch.age_group}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              )}

              {formData.target_audience === 'individual' && (
                <TextField
                  select
                  label="Select Student *"
                  name="student_id"
                  value={formData.student_id}
                  onChange={handleInputChange}
                  fullWidth
                  helperText={students.length === 0 ? "No students available" : ""}
                >
                  {students.length === 0 ? (
                    <MenuItem value="" disabled>No students available</MenuItem>
                  ) : (
                    students.map((student) => (
                      <MenuItem key={student.id} value={student.id}>
                        {student.student_name} - {student.email}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editingNotification ? 'Update' : 'Send'} Notification
            </Button>
          </DialogActions>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem onClick={() => {
            handleOpenDialog(selectedNotification);
            handleCloseMenu();
          }}>
            Edit
          </MenuItem>
          <MenuItem onClick={() => handleArchiveToggle(selectedNotification)}>
            {selectedNotification?.is_archived ? (
              <>
                <UnarchiveIcon sx={{ mr: 1 }} fontSize="small" />
                Unarchive
              </>
            ) : (
              <>
                <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
                Archive
              </>
            )}
          </MenuItem>
          <MenuItem onClick={handleDeleteNotification} sx={{ color: 'error.main' }}>
            Delete
          </MenuItem>
        </Menu>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </AdminLayout>
  );
};

export default Notifications;
